import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { api, imgUrl } from "@/api";
import Marquee from "@/components/Marquee";
import { FadeUp, MaskedLine } from "@/components/Reveal";

const HERO_ID = "dsc01829";
const FEATURED_IDS = ["img-4872", "dsc01829", "dsc-0093", "dsc01178"];

const CHAPTERS = [
  {
    n: "01",
    title: "Light & Shadow",
    img: "dsc01176",
    text: "Low-key frames where darkness does half the talking. I shoot for the edges — the rim light on a shoulder, the glow under a chassis.",
  },
  {
    n: "02",
    title: "Speed & Silhouette",
    img: "dsc-0137",
    text: "Cars are characters. Parked at a meet or idling under streetlights, every machine gets treated like a portrait sitting.",
  },
  {
    n: "03",
    title: "Intentional Freedom",
    img: "dsc01830",
    text: "Golden hour on the rocks, nothing staged beyond the light itself. Personal work that feels like the memory, not the pose.",
  },
];

export default function Home() {
  const [photos, setPhotos] = useState([]);
  const [spot, setSpot] = useState({ x: 700, y: 300 });
  const heroRef = useRef(null);

  useEffect(() => {
    api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const byId = (id) => photos.find((p) => p.id === id);
  const heroImg = byId(HERO_ID);
  const featured = FEATURED_IDS.map(byId).filter(Boolean);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.28]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <main data-testid="home-page">
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative h-[calc(100svh-4rem-env(safe-area-inset-top))] overflow-hidden"
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setSpot({ x: e.clientX - r.left, y: e.clientY - r.top });
        }}
      >
        <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
          {heroImg && (
            <img src={imgUrl(heroImg.src)} alt="Golden hour silhouette on the lakeshore" className="h-full w-full object-cover object-[25%_center] md:object-center" />
          )}
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/70" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(520px at ${spot.x}px ${spot.y}px, rgba(212,74,58,0.14), transparent 70%)` }}
        />
        <div className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-8 lg:px-16 pb-12">
          <motion.div style={{ opacity: fade }}>
            <MaskedLine delay={0.15}>
              <span className="font-mono font-bold text-xs sm:text-sm uppercase tracking-[0.35em] text-flame">
                Car & Personal Photography
              </span>
            </MaskedLine>
            <h1 className="mt-4 font-serif leading-[0.85] text-bone" data-testid="hero-headline">
              <MaskedLine delay={0.3}>
                <span className="block text-[17vw] lg:text-[11rem] tracking-tight">MADDIE</span>
              </MaskedLine>
              <MaskedLine delay={0.45}>
                <span className="block text-[17vw] lg:text-[11rem] tracking-tight italic text-outline">REISS</span>
              </MaskedLine>
            </h1>
            <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
              <MaskedLine delay={0.65}>
                <p className="max-w-md text-sm sm:text-base text-ash leading-relaxed">
                  Machines after midnight. People in golden light. Every frame shot with intention — nothing accidental.
                </p>
              </MaskedLine>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto"
              >
                <Link
                  to="/gallery"
                  data-testid="hero-cta-gallery"
                  className="bg-flame text-ink px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-center hover:bg-flamebright transition-colors duration-300"
                >
                  View the work
                </Link>
                <Link
                  to="/book"
                  data-testid="hero-cta-book"
                  className="border border-white/25 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone text-center hover:border-flame hover:text-flame transition-colors duration-300"
                >
                  Book a session
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
        <motion.div
          className="absolute bottom-6 right-6 hidden lg:flex flex-col items-end gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-faint z-10"
          style={{ opacity: fade }}
        >
          <span>Portfolio 2026</span>
          <span>39 frames / 2 worlds</span>
        </motion.div>
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-faint"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          style={{ opacity: fade }}
        >
          <ArrowDown size={16} />
        </motion.div>
      </section>

      <Marquee />

      <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28" data-testid="selected-frames">
        <FadeUp>
          <div className="flex items-end justify-between border-b border-white/10 pb-6 mb-10">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Selected frames</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bone">Two worlds, one eye</h2>
            </div>
            <Link
              to="/gallery"
              data-testid="selected-view-all"
              className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash hover:text-flame transition-colors"
            >
              Full gallery <ArrowUpRight size={14} />
            </Link>
          </div>
        </FadeUp>
        <div className="grid grid-cols-12 gap-5">
          {featured.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.08} className={i % 2 === 0 ? "col-span-12 md:col-span-7" : "col-span-12 md:col-span-5"}>
              <Link
                to={`/gallery?cat=${p.category}`}
                data-testid={`featured-${p.id}`}
                className="tick-frame group block border border-white/10 overflow-hidden bg-coal"
              >
                <div className="overflow-hidden aspect-[4/3]">
                  <img
                    src={imgUrl(p.thumb)}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center justify-between p-4 gap-4">
                  <span className="font-serif text-xl text-bone">{p.title}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint text-right">{p.subcategory}</span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 py-20 lg:py-28 border-t border-white/10" data-testid="manifesto">
        <FadeUp>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Manifesto</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bone mb-14">How I see</h2>
        </FadeUp>
        <div>
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.05}>
              <div
                className="group grid lg:grid-cols-12 gap-6 items-center border-t border-white/10 py-10"
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <span className="lg:col-span-1 font-mono text-sm text-flame">{c.n}</span>
                <h3 className="lg:col-span-4 font-serif text-3xl sm:text-4xl text-bone transition-all duration-300 group-hover:italic group-hover:text-flame">
                  {c.title}
                </h3>
                <p className="lg:col-span-4 text-sm text-ash leading-relaxed">{c.text}</p>
                <div className="lg:col-span-3 order-first lg:order-none h-60 sm:h-72 lg:h-44 overflow-hidden border border-white/10 lg:opacity-60 group-hover:opacity-100 transition-opacity duration-500">
                  {byId(c.img) && (
                    <img src={imgUrl(byId(c.img).src)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  )}
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-16 py-24 lg:py-32 border-t border-white/10" data-testid="cta-band">
        <FadeUp>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-6">Book a session</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bone max-w-4xl">
            A car that owns the night. <em className="text-flame">A face that owns the light.</em> Let&apos;s make the frame.
          </h2>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/book"
              data-testid="cta-book-button"
              className="bg-flame text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors duration-300"
            >
              Schedule a session
            </Link>
            <Link
              to="/gallery"
              data-testid="cta-gallery-link"
              className="border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone hover:border-flame hover:text-flame transition-colors duration-300"
            >
              Browse the work
            </Link>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
